import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import analytics from '../utils/analytics';
import {
  APP_STORE_URL,
  getPlayStoreUrl,
  handleAppStoreClick,
} from '../utils/appStoreLinks';
// Custom-scheme fallback when the native app is installed but universal
// links didn't intercept (e.g. long-pressed "open in browser").
const APP_SCHEME_BASE = 'crappermapperapp://inappinvite';

/**
 * Invite landing page (`/inappinvite?ref=CODE`, the canonical invite URL
 * shared by web + mobile).
 *
 * Reached only when the native app did NOT intercept the link (universal
 * links open the app directly when installed). Stores the code for signup,
 * shows it, and offers Open-in-App + store download paths.
 */
function InAppInvite() {
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const code = searchParams.get('ref');

    if (code) {
      const normalized = code.trim().toUpperCase();
      // Store referral code for later use during signup
      localStorage.setItem('referralCodeUsed', normalized);
      setReferralCode(normalized);

      // Fire analytics event for tracking
      analytics.trackReferralVisit(normalized, window.location.href);

      // Track invite link opened in PostHog
      analytics.track('invite_link_opened', {
        referral_code: normalized,
      });

      console.log(`Referral code stored: ${normalized}`);
    }
  }, [searchParams]);

  const openInAppHref = referralCode
    ? `${APP_SCHEME_BASE}?ref=${encodeURIComponent(referralCode)}`
    : 'crappermapperapp://';

  // iOS has no install-referrer API, so the App Store badge doubles as the
  // handoff: copy the code on the way out (fire-and-forget — navigation
  // must never wait on the clipboard). The user pastes it at app signup.
  const copyCodeOnStoreClick = async () => {
    await handleAppStoreClick();
  };

  const copyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.warn('Clipboard copy failed:', error);
    }
  };

  // Pass the invite through the Play Store so Android can attribute the
  // install back to the referrer via the Play Install Referrer API (the
  // value arrives verbatim as the `referrer` string).
  const playStoreHref = getPlayStoreUrl(referralCode);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img
          src="/app-icon.png"
          alt="The Crapper Mapper"
          style={styles.logo}
        />
        <h1 style={styles.title}>You&apos;ve Been Invited!</h1>
        <p style={styles.subtitle}>
          Find the cleanest bathrooms nearby, reviewed by real people.
        </p>

        {referralCode ? (
          <div style={styles.codeBox}>
            <span style={styles.codeLabel}>Your invite code</span>
            <span style={styles.code}>{referralCode}</span>
            <button type="button" style={styles.copyButton} onClick={copyCode}>
              {copied ? 'Copied!' : 'Copy code'}
            </button>
            <span style={styles.codeNote}>
              It&apos;ll be applied automatically when you sign up.
            </span>
          </div>
        ) : null}

        <a style={styles.openAppButton} href={openInAppHref}>
          Open in the App
        </a>
        <p style={styles.orText}>or get the app first — your invite is saved</p>

        <div style={styles.storeRow}>
          <a href={APP_STORE_URL} onClick={copyCodeOnStoreClick}>
            <img
              src="/assets/badges/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
              alt="Download on the App Store"
              style={styles.storeBadge}
            />
          </a>
          <a href={playStoreHref}>
            <img
              src="/assets/badges/GetItOnGooglePlay_Badge_Web_color_English.png"
              alt="Get it on Google Play"
              style={styles.storeBadge}
            />
          </a>
        </div>

        <Link to="/" style={styles.homeLink}>
          Continue to the website
        </Link>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f1ec',
    fontFamily: 'Arial, sans-serif',
    padding: '20px',
  },
  card: {
    maxWidth: '420px',
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '32px 24px',
    textAlign: 'center',
    boxShadow: '0 4px 16px rgba(97, 79, 72, 0.15)',
  },
  logo: {
    width: '72px',
    height: '72px',
    borderRadius: '16px',
  },
  title: {
    color: '#5752e8',
    margin: '16px 0 8px',
    fontSize: '1.6rem',
  },
  subtitle: {
    color: '#666',
    margin: '0 0 8px',
  },
  codeBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    backgroundColor: '#f5f1ec',
    borderRadius: '12px',
    padding: '14px',
    margin: '12px 0 4px',
  },
  codeLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#5752e8',
  },
  code: {
    fontSize: '1.8rem',
    fontWeight: '800',
    letterSpacing: '0.2em',
    color: '#5752e8',
  },
  codeNote: {
    fontSize: '0.8rem',
    color: '#666',
  },
  copyButton: {
    alignSelf: 'center',
    marginTop: '8px',
    padding: '8px 20px',
    backgroundColor: '#614f48',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '0.85rem',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  openAppButton: {
    display: 'block',
    marginTop: '16px',
    padding: '14px',
    backgroundColor: '#614f48',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '1rem',
    borderRadius: '10px',
    textDecoration: 'none',
  },
  orText: {
    fontSize: '0.85rem',
    color: '#666',
    margin: '12px 0',
  },
  storeRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  storeBadge: {
    height: '48px',
  },
  homeLink: {
    display: 'inline-block',
    marginTop: '16px',
    color: '#5752e8',
    fontSize: '0.9rem',
  },
};

export default InAppInvite;
